import type { ReactNode } from 'react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * ENT-4269 (D3) — the shared sign-in/auth card chrome.
 *
 * The card shell (`max-w-md` bordered card + centred header with the mobile
 * logo, title and subtitle) was duplicated three times: the live auth card and
 * the email-sent view in `Auth.tsx`, plus a hand-rolled copy inside the
 * `SignIn` Storybook story — so the story could silently drift from production.
 * `AuthCard` is the single source of that chrome; `Auth.tsx` and the stories
 * both render through it.
 */
export interface AuthCardProps {
  title: ReactNode;
  description?: ReactNode;
  /**
   * Icon shown in a circular badge above the title (e.g. the email-sent view's
   * MailCheck). When omitted, the mobile-only Supply Guard logo is shown — the
   * wide-screen marketing pane already carries the brand on `lg` and up.
   */
  icon?: ReactNode;
  /** Extra class names for the `CardDescription` (e.g. the larger email-sent copy). */
  descriptionClassName?: string;
  /** Node rendered directly beneath the description (e.g. the destination email on the email-sent view). */
  belowDescription?: ReactNode;
  /** Extra class names for the `CardContent` wrapper (e.g. `space-y-6`). */
  contentClassName?: string;
  'data-testid'?: string;
  children: ReactNode;
}

export function AuthCard({
  title,
  description,
  icon,
  descriptionClassName,
  belowDescription,
  contentClassName,
  children,
  ...rest
}: AuthCardProps) {
  return (
    <Card className="w-full max-w-md border-border/50 shadow-xl" data-testid={rest['data-testid']}>
      <CardHeader className="pb-5 text-center sm:pb-6">
        {icon ? (
          <div className="mb-6 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-primary/20 bg-gradient-to-br from-primary/20 to-primary/10">
              {icon}
            </div>
          </div>
        ) : (
          <div className="mb-4 flex justify-center lg:hidden">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/60">
              <img src="/platform-logo-white.svg" alt="Supply Guard" className="h-6 w-6" />
            </div>
          </div>
        )}
        <CardTitle className="text-2xl">{title}</CardTitle>
        {description ? <CardDescription className={descriptionClassName}>{description}</CardDescription> : null}
        {belowDescription ?? null}
      </CardHeader>
      <CardContent className={contentClassName}>{children}</CardContent>
    </Card>
  );
}
